import type { ManagementUser } from '../core/_models'

/** Line shown in primary row of the user picker. */
export function getUserPrimaryLabel(u: ManagementUser): string {
  const name = (u.fullName || '').trim()
  if (name) return name
  const un = (u.username || '').trim()
  if (un) return un
  const em = (u.email || '').trim()
  if (em) return em
  return `User #${u.userId}`
}

function formatRoleType(rt: string | undefined | null): string {
  if (!rt) return 'Unknown role'
  return rt
    .split('_')
    .map((p) => p.charAt(0) + p.slice(1).toLowerCase())
    .join(' ')
}

/** Sub-line: `username | Role | Company` (parts omitted if missing). */
export function getUserSecondaryLine(u: ManagementUser): string {
  const parts: string[] = []
  if ((u.username || '').trim()) parts.push(u.username.trim())
  const role = formatRoleType(u.roleType || u.roleName)
  if (role && role !== 'Unknown role') parts.push(role)
  if ((u.companyName || '').trim()) parts.push(u.companyName!.trim())
  return parts.length ? parts.join(' | ') : '—'
}

export type DerivedDataScope = {
  /** Short line for summary cards */
  summary: string
  /** Longer explanation for the Data scope tab */
  description: string
}

/**
 * How far this person’s data *view* reaches — fixed from account/role, not from manual scope edits.
 * Aligns to product: scope comes from user type and ownership, not the Admin access UI.
 */
export function deriveDataViewingScope(u: ManagementUser | null): DerivedDataScope {
  if (!u) {
    return {
      summary: '—',
      description: 'Select someone above. Their data viewing scope is determined by their account type and how they are set up in the system.',
    }
  }
  const rt = (u.roleType || u.roleName || '').toUpperCase()
  const company = (u.companyName || '').trim()
  const hasCompany = company.length > 0

  switch (rt) {
    case 'SUPER_ADMIN':
      return {
        summary: 'All organizations',
        description:
          'Super administrators can access data across the system as defined by your platform rules. This is not something you set on this page.',
      }
    case 'COMPANY_GROUP_ADMIN':
      return {
        summary: 'Own company group',
        description:
          'Company group admins work within their own group’s data. Scope is defined by the account, not on this screen.',
      }
    case 'COMPANY_ADMIN':
      return {
        summary: 'Own company only',
        description: hasCompany
          ? `Company administrators see data for their own company only (${company}).`
          : 'Company administrators see data for their own company only. Company context comes from the account record.',
      }
    case 'SHORE':
      return {
        summary: 'Own company (shore)',
        description: hasCompany
          ? `Shore/company user: data is limited to the company you belong to (${company}).`
          : 'Shore/company user: data is limited to the company linked to this account.',
      }
    case 'CREW':
      return {
        summary: 'Own company & vessel',
        description:
          'Crew accounts can see their own company and their assigned vessel, as set up in crewing — not from this access screen.',
      }
    case 'OPERATOR': {
      const isGlobal = !hasCompany
      if (isGlobal) {
        return {
          summary: 'All organizations (global operator)',
          description:
            'This operator is not limited to a single company in the list and is treated as a global/central operator for data they are allowed to see, per your rules.',
        }
      }
      return {
        summary: 'Own company (operator)',
        description: `This operator is tied to a company; their data view is limited to that organization (${company}).`,
      }
    }
    case 'VENDOR':
      return {
        summary: 'Linked company (vendor)',
        description: hasCompany
          ? `Vendors are scoped to the company they are linked to (${company}).`
          : 'Vendors are scoped to the company they are linked to in the system.',
      }
    default:
      return {
        summary: 'Based on account type',
        description: `Role: ${rt || 'Unknown'}. What they can see is determined by the account and your system configuration, not by manual data-scope settings on this page.`,
      }
  }
}

export function uniqueRoleTypesFromUsers(users: ManagementUser[]): string[] {
  const s = new Set<string>()
  users.forEach((u) => {
    const r = (u.roleType || u.roleName || '').trim()
    if (r) s.add(r)
  })
  return Array.from(s).sort((a, b) => a.localeCompare(b))
}

export function uniqueCompanyNamesFromUsers(users: ManagementUser[]): string[] {
  const s = new Set<string>()
  users.forEach((u) => {
    const c = (u.companyName || '').trim()
    if (c) s.add(c)
  })
  return Array.from(s).sort((a, b) => a.localeCompare(b))
}
